import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  IconButton,
  Divider,
  Paper,
  Chip,
  Alert,
  Grid,
} from "@mui/material";
import {
  Close as CloseIcon,
  Send as SendIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { useCreate, useCustom } from "@refinedev/core";
import { ICharity } from "interfaces/charity";
import UsersEmailSelection from "components/inputs/fund-users-email-drop-down";
import { IUser } from "interfaces/user";

const templates = {
  wire: {
    subject: "Grant Notification",
    body: "We are pleased to confirm that the grant described in the attached was just processed via a wire transfer.",
  },
  chariot: {
    subject: "Grant Notification",
    body: "We are pleased to confirm that the grant described in the attached was just submitted. Payment will be processed by our partner Chariot. If you don't have an account with them yet, we suggest you sign up so that you can receive funds via electronic transfer (faster and safer) instead of via a check in the mail.",
  },
};

const fixedFooter =
  "Do not hesitate to contact us if you have any questions or need additional information.\n\nNeta Foundation";

type TemplateKey = keyof typeof templates;

interface NotifyCharityModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  scheduledGrantRequestId: number;
  charityEin: string;
  fundAccountId?: number;
}

const NotifyCharityModal: React.FC<NotifyCharityModalProps> = ({
  open,
  setOpen,
  scheduledGrantRequestId,
  charityEin,
  fundAccountId
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateKey>("wire");
  const [recipientEmails, setRecipientEmails] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [emailError, setEmailError] = useState<string>("");

  const { data: charityData, refetch } = useCustom<ICharity>({
    url: `storedCharity/${charityEin}`,
    method: "get",
  });

  const { mutate, isLoading } = useCreate();
  const [existingEmails, setExistingEmails] = useState<string[]>([]);

  const [selectedUsers, setSelectedUsers] = useState<IUser[]>([]);
  // Initialize with suggested emails when charity data loads
  useEffect(() => {
    if (
      charityData?.data?.emailToNotify &&
      Array.isArray(charityData.data.emailToNotify) &&
      open
    ) {
      const suggestedEmails = charityData.data.emailToNotify;
      // Only add emails that aren't already in the list
      const newEmails = suggestedEmails.filter(
        (email) => !recipientEmails.includes(email)
      );
      if (newEmails.length > 0) {
        setRecipientEmails([...recipientEmails, ...newEmails]);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [charityData, open]);

  useEffect(() => {
    if (selectedTemplate && templates[selectedTemplate]) {
      setSubject(templates[selectedTemplate].subject as string);
      setBody(templates[selectedTemplate].body as string);
    }
  }, [selectedTemplate]);

  const handleClose = () => {
    setOpen(false);
    // Reset state on close
    setRecipientEmails([]);
    setNewEmail("");
    setEmailError("");
  };

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const emailExists = (email: string) => {
    // Case-insensitive check for duplicate emails
    return recipientEmails.some(
      (existingEmail) => existingEmail.toLowerCase() === email.toLowerCase()
    );
  };

  const handleAddEmail = () => {
    const email = newEmail.trim();

    // Reset error
    setEmailError("");

    // Validate email format
    if (!email) {
      setEmailError("Please enter an email address");
      return;
    }

    if (!isValidEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    // Check for duplicates
    if (emailExists(email)) {
      setEmailError("This email is already in the recipient list");
      return;
    }

    // Add email if all checks pass
    setRecipientEmails([...recipientEmails, email]);
    setNewEmail("");
    setEmailError("");
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    setRecipientEmails(
      recipientEmails.filter((email) => email !== emailToRemove)
    );
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleAddEmail();
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewEmail(e.target.value);
    // Clear error when user starts typing
    if (emailError) {
      setEmailError("");
    }
  };

  const handleSend = () => {
    mutate(
      {
        resource: "notify-charity",
        values: {
          recipientEmail: recipientEmails,
          subject: subject,
          templateType: selectedTemplate,
          body: body,
          scheduledGrantRequestId: scheduledGrantRequestId,
          cc: existingEmails
        },
      },
      {
        onSuccess: () => {
          refetch();
          handleClose();
        },
      }
    );
  };

  const handleTemplateChange = (event: any) => {
    setSelectedTemplate(event.target.value);
  };

  const suggestedEmails = charityData?.data?.emailToNotify || [];
  const trimmedEmail = newEmail.trim();
  const isDuplicateEmail = trimmedEmail && emailExists(trimmedEmail);
  const isInvalidEmail = trimmedEmail && !isValidEmail(trimmedEmail);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="notify-charity-modal-title"
      aria-describedby="notify-charity-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", sm: 600, md: 700 },
          maxHeight: "90vh",
          overflow: "hidden",
          outline: "none",
        }}
      >
        <Paper
          elevation={24}
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            maxHeight: "90vh",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 3,
              pb: 2,
            }}
          >
            <Typography
              id="notify-charity-modal-title"
              variant="h5"
              component="h2"
              fontWeight="medium"
            >
              Notify Charity
            </Typography>
            <IconButton
              onClick={handleClose}
              size="small"
              sx={{
                color: "text.secondary",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider />

          <Box
            sx={{
              p: 3,
              flex: 1,
              overflow: "auto",
            }}
          >
            <Stack spacing={3}>
              {/* Suggested Emails Info */}
              {suggestedEmails.length > 0 && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>
                      Suggested emails from previous charity email
                      notifications:
                    </strong>
                  </Typography>
                  {suggestedEmails.map((email, index) => (
                    <Typography key={index} variant="body2" component="div">
                      • {email}
                    </Typography>
                  ))}
                </Alert>
              )}

              {/* Selected Emails Display */}
              {recipientEmails.length > 0 && (
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Recipients ({recipientEmails.length}):
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {recipientEmails.map((email) => (
                      <Chip
                        key={email}
                        label={email}
                        onDelete={() => handleRemoveEmail(email)}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Add New Email Input */}
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  fullWidth
                  label="Add Recipient Email"
                  type="email"
                  value={newEmail}
                  onChange={handleEmailChange}
                  onKeyPress={handleKeyPress}
                  placeholder="charity@example.org"
                  variant="outlined"
                  helperText={
                    emailError ||
                    (isDuplicateEmail
                      ? "This email is already in the list"
                      : "Enter email and click Add or press Enter")
                  }
                />
                <Button
                  variant="contained"
                  onClick={handleAddEmail}
                  disabled={
                    !Boolean(trimmedEmail) ||
                    Boolean(isInvalidEmail) ||
                    Boolean(isDuplicateEmail)
                  }
                  startIcon={<AddIcon />}
                  sx={{ minWidth: 100, height: 56 }}
                >
                  Add
                </Button>
              </Box>
              {fundAccountId && (
                <Grid container spacing={1} pt={1}>
                  <UsersEmailSelection
                    key={existingEmails.join(",")}
                    fundAccountId={
                      fundAccountId as number
                    }
                    value={selectedUsers}
                    setSelectedUser={setSelectedUsers}
                    showError={undefined}
                    filterEmails={[]}
                    selectedAccount={fundAccountId}
                    onFilterEmailsChange={setExistingEmails}
                    onEmailsChange={(emails) => setExistingEmails(emails)}
                    isCC={true}
                  />
                </Grid>
              )}
              <FormControl fullWidth>
                <InputLabel id="template-select-label">Template</InputLabel>
                <Select
                  labelId="template-select-label"
                  id="template-select"
                  value={selectedTemplate}
                  label="Template"
                  onChange={handleTemplateChange}
                >
                  <MenuItem value="wire">Wire Template</MenuItem>
                  <MenuItem value="chariot">Chariot Template</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                variant="outlined"
                required
              />

              <TextField
                fullWidth
                label="Message Body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                variant="outlined"
                multiline
                rows={6}
                required
                helperText="Enter the main message content"
              />

              <TextField
                fullWidth
                label="Signature (Auto-included)"
                value={fixedFooter}
                variant="outlined"
                multiline
                rows={3}
                disabled
                helperText="This signature will be automatically added to all messages"
                sx={{
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "rgba(0, 0, 0, 0.6)",
                  },
                }}
              />
            </Stack>
          </Box>

          <Divider />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 3,
              pt: 2,
              backgroundColor: "grey.50",
            }}
          >
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{ minWidth: 100 }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={handleSend}
              startIcon={<SendIcon />}
              disabled={
                recipientEmails.length === 0 || !subject || !body || isLoading
              }
              sx={{ minWidth: 120 }}
            >
              Send Notification
            </Button>
          </Box>
        </Paper>
      </Box>
    </Modal>
  );
};

export default NotifyCharityModal;
